import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Loader2, Check, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { paymentService } from '@/api/paymentService';
import { useAuth } from '@/context/AuthContext';
import AuthModal from '@/components/AuthModal';

type BillingInterval = 'month' | 'year';
type Tier = 'small' | 'big';

const SMALL_FEATURES = [
    'Access all Features',
    'Weekly Progress Reports',
    'Unlimited LLMs.txts',
    'Optimize and Track Up to 10 Queries',
    '30-Day Moneyback Guarantee',
];

const BIG_FEATURES = [
    'Access all Features',
    'Weekly Progress Reports',
    'Unlimited LLMs.txt (No Page Limit)',
    'Optimize and Track Up to 200 Queries',
    '30-Day Moneyback Guarantee',
];

interface SubscriptionModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: () => void;
}

export default function SubscriptionModal({ open, onOpenChange }: SubscriptionModalProps) {
    const [billingInterval, setBillingInterval] = useState<BillingInterval>('year');
    const [authOpen, setAuthOpen] = useState(false);
    const [pendingPriceId, setPendingPriceId] = useState<string | null>(null);
    const { userSession, isAuthenticated } = useAuth();

    const { data: subscription, isLoading: subLoading } = useQuery({
        queryKey: ['subscription'],
        queryFn: paymentService.getSubscription,
        enabled: open && isAuthenticated,
        retry: false,
    });

    const { data: plans, isLoading: plansLoading } = useQuery({
        queryKey: ['subscription-plans'],
        queryFn: paymentService.getSubscriptionPlans,
        enabled: open,
    });

    const checkoutMutation = useMutation({
        mutationFn: paymentService.createCheckoutSession,
        onSuccess: (data) => {
            window.location.href = data.url;
        },
    });

    const portalMutation = useMutation({
        mutationFn: paymentService.getPortalUrl,
        onSuccess: (data) => {
            window.location.href = data.url;
        },
    });

    // Get plans by tier and interval
    const getPlan = (tier: Tier, interval: BillingInterval) => {
        return plans?.find((p) => p.tier === tier && p.interval === interval);
    };

    const smallMonthlyPlan = getPlan('small', 'month');
    const smallYearlyPlan = getPlan('small', 'year');
    const bigMonthlyPlan = getPlan('big', 'month');
    const bigYearlyPlan = getPlan('big', 'year');

    // Calculate prices
    const smallMonthlyPrice = smallMonthlyPlan ? smallMonthlyPlan.amount / 100 : 29;
    const smallYearlyPrice = smallYearlyPlan ? smallYearlyPlan.amount / 100 : 300;
    const bigMonthlyPrice = bigMonthlyPlan ? bigMonthlyPlan.amount / 100 : 99;
    const bigYearlyPrice = bigYearlyPlan ? bigYearlyPlan.amount / 100 : 996;

    const smallDisplayPrice = billingInterval === 'year' ? Math.round(smallYearlyPrice / 12) : smallMonthlyPrice;
    const bigDisplayPrice = billingInterval === 'year' ? Math.round(bigYearlyPrice / 12) : bigMonthlyPrice;

    const isSubscribed = userSession?.subscription?.status === 'active' || userSession?.subscription?.status === 'trialing';

    const handleSubscribe = (tier: Tier) => {
        const plan = getPlan(tier, billingInterval);
        if (!plan) return;

        if (!isAuthenticated) {
            setPendingPriceId(plan.stripePriceId);
            setAuthOpen(true);
            return;
        }

        checkoutMutation.mutate(plan.stripePriceId);
    };

    const handleAuthSuccess = () => {
        setAuthOpen(false);
        if (pendingPriceId) {
            checkoutMutation.mutate(pendingPriceId);
            setPendingPriceId(null);
        }
    };

    // Loading state
    if (subLoading && isAuthenticated) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-[420px] p-8">
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    </div>
                </DialogContent>
                <AuthModal
                    open={authOpen}
                    onOpenChange={setAuthOpen}
                    onSuccess={handleAuthSuccess}
                />
            </Dialog>
        );
    }

    // Subscribed user view
    if (isSubscribed && subscription) {
        const currentPlan = plans?.find(p => p.stripePriceId === subscription.priceId);
        const tierLabel = currentPlan?.tier === 'big' ? 'Big Business' : 'Small Business';
        const intervalLabel = currentPlan?.interval === 'year' ? 'Yearly' : 'Monthly';

        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-[420px] p-8 gap-0">
                    <div className="flex flex-col gap-6">
                        <div className="flex flex-col items-center gap-2">
                            <h2 className="text-xl font-bold text-center">Account</h2>
                            <p className="text-sm text-muted-foreground text-center">
                                {tierLabel} • {intervalLabel} • Active
                            </p>
                        </div>

                        <div className="flex flex-col border border-border rounded overflow-hidden">
                            <button
                                onClick={() => portalMutation.mutate()}
                                disabled={portalMutation.isPending}
                                className="flex items-center justify-between py-4 px-4 hover:bg-muted transition-colors disabled:opacity-50 text-left"
                            >
                                <span className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground">
                                    Billing
                                </span>
                                {portalMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                            </button>
                            <div className="bg-border h-px w-full" />
                            <a
                                href="https://mail.google.com/mail/?view=cm&to=support@byllms.com&su=WE%20HAVE%20TO%20TALK&body=Call%20me%20asap."
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-between py-4 px-4 hover:bg-muted transition-colors text-left"
                            >
                                <span className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground">
                                    Contact Support
                                </span>
                            </a>
                            <div className="bg-border h-px w-full" />
                            <button
                                onClick={() => portalMutation.mutate()}
                                disabled={portalMutation.isPending}
                                className="flex items-center justify-between py-4 px-4 hover:bg-muted transition-colors disabled:opacity-50 text-left"
                            >
                                <span className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-red-600">
                                    Cancel Subscription
                                </span>
                                {portalMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                            </button>
                        </div>
                    </div>
                </DialogContent>
                <AuthModal
                    open={authOpen}
                    onOpenChange={setAuthOpen}
                    onSuccess={handleAuthSuccess}
                />
            </Dialog>
        );
    }

    // Subscription plans view
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[720px] p-6 md:p-8 gap-0">
                <div className="flex flex-col items-center gap-6">
                    <Sparkles className="w-6 h-6 text-foreground" />

                    <h2 className="text-xl leading-[1.1] text-center font-condensed font-bold uppercase">
                        Subscribe to unlock all features
                    </h2>

                    {/* Billing Toggle */}
                    <div className="relative flex w-full max-w-md border border-border rounded overflow-hidden">
                        <div
                            className="absolute top-0 bottom-0 w-1/2 bg-muted/50 transition-transform duration-200 ease-out"
                            style={{ transform: billingInterval === 'year' ? 'translateX(100%)' : 'translateX(0)' }}
                        />
                        <button
                            type="button"
                            onClick={() => setBillingInterval('month')}
                            className={`relative flex-1 py-3 font-mono text-xs tracking-[0.6px] uppercase border-r border-border transition-colors duration-200 ${
                                billingInterval === 'month'
                                    ? 'text-foreground'
                                    : 'text-muted-foreground'
                            }`}
                        >
                            Monthly
                        </button>
                        <button
                            type="button"
                            onClick={() => setBillingInterval('year')}
                            className={`relative flex-1 py-3 font-mono text-xs tracking-[0.6px] uppercase transition-colors duration-200 ${
                                billingInterval === 'year'
                                    ? 'text-primary'
                                    : 'text-muted-foreground'
                            }`}
                        >
                            Annual <span className="text-primary">(Save 20%)</span>
                        </button>
                    </div>

                    {/* Pricing Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                        {/* Small Business Card */}
                        <div className="border border-border rounded-lg p-6 flex flex-col">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-bold text-lg uppercase tracking-wide">Small Business</h3>
                                <span className="text-xs text-muted-foreground font-mono uppercase tracking-wide">
                                    Billed {billingInterval === 'year' ? 'annually' : 'monthly'}
                                </span>
                            </div>

                            <div className="mb-4">
                                <div className="flex items-baseline">
                                    <span
                                        key={`small-${smallDisplayPrice}`}
                                        className="text-3xl font-bold animate-[slideUp_0.2s_ease-out]"
                                    >
                                        ${smallDisplayPrice}
                                    </span>
                                    <span className="text-base text-muted-foreground">/mo</span>
                                </div>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Perfect for small businesses who want to start getting indexed in AI searches.
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => handleSubscribe('small')}
                                disabled={checkoutMutation.isPending || plansLoading}
                                className="w-full h-12 border border-foreground text-foreground font-mono text-sm tracking-[0.6px] uppercase rounded hover:bg-muted transition-colors disabled:opacity-50 flex items-center justify-center mb-6"
                            >
                                {checkoutMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    'Subscribe'
                                )}
                            </button>

                            <div className="space-y-3 mt-auto">
                                {SMALL_FEATURES.map((feature) => (
                                    <div key={feature} className="flex items-start gap-3">
                                        <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                                        <span className="text-sm leading-snug">{feature}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Big Business Card */}
                        <div className="border-2 border-primary rounded-lg p-6 flex flex-col">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-bold text-lg uppercase tracking-wide">Big Business</h3>
                                <span className="text-xs text-muted-foreground font-mono uppercase tracking-wide">
                                    Billed {billingInterval === 'year' ? 'annually' : 'monthly'}
                                </span>
                            </div>

                            <div className="mb-4">
                                <div className="flex items-baseline">
                                    <span
                                        key={`big-${bigDisplayPrice}`}
                                        className="text-3xl font-bold animate-[slideUp_0.2s_ease-out]"
                                    >
                                        ${bigDisplayPrice}
                                    </span>
                                    <span className="text-base text-muted-foreground">/mo</span>
                                </div>
                                <p className="text-sm text-muted-foreground mt-2">
                                    Ideal for bigger businesses that need to grow and scale their brand through AI Searches
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={() => handleSubscribe('big')}
                                disabled={checkoutMutation.isPending || plansLoading}
                                className="w-full h-12 bg-primary text-white font-mono text-sm tracking-[0.6px] uppercase rounded hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center mb-6"
                            >
                                {checkoutMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    'Subscribe'
                                )}
                            </button>

                            <div className="space-y-3 mt-auto">
                                {BIG_FEATURES.map((feature) => (
                                    <div key={feature} className="flex items-start gap-3">
                                        <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                                        <span className="text-sm leading-snug">{feature}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <p className="text-center font-mono text-[10px] tracking-[0.5px] uppercase text-muted-foreground leading-relaxed">
                        By subscribing you agree to our{' '}
                        <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">
                            Service Terms
                        </Link>{' '}
                        and{' '}
                        <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">
                            Privacy Policy
                        </Link>
                    </p>
                </div>

                <style>{`
                    @keyframes slideUp {
                        from {
                            opacity: 0;
                            transform: translateY(8px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }
                `}</style>
            </DialogContent>
            <AuthModal
                open={authOpen}
                onOpenChange={setAuthOpen}
                onSuccess={handleAuthSuccess}
            />
        </Dialog>
    );
}
